/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;

        // Update inventory for the selected variant
        const inventoryComponent = this.querySelector('product-inventory');
        if (inventoryComponent) {
          const variantInventory = JSON.parse(inventoryComponent.querySelector('[type="application/json"]').textContent)
            .find(variant => variant.id === evt.detail.variant.id);
            
          if (variantInventory) {
            inventoryComponent.setAttribute('data-inventory-quantity', variantInventory.inventory_quantity);
            inventoryComponent.setAttribute('data-variant-available', variantInventory.available);
            inventoryComponent.setAttribute('data-inventory-policy', variantInventory.inventory_policy);
            
            // Update the inventory display
            inventoryComponent.updateInventory(
              variantInventory.inventory_quantity,
              variantInventory.available,
              variantInventory.inventory_policy
            );
          }
        }
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight');
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      // Get the complete product-inventory element and ensure it's visible
      const inventoryElem = this.productEl.querySelector('product-inventory');
      let inventoryHtml = '';
      if (inventoryElem) {
        inventoryElem.classList.remove('no-js-hidden');
        inventoryHtml = inventoryElem.outerHTML;
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media${theme.settings.blendProductImages ? ' image-blend' : ''}"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            ${weightElem}
            <hr>
            <div class="product-price">
              ${this.getElementHtml('.product-price')}
            </div>
            ${inventoryHtml}
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.getElementHtml('.product-options', '.custom-option:not(.quick-add-hidden)')}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;

      // Re-initialize the product-inventory component
      const newInventoryElem = this.content.querySelector('product-inventory');
      if (newInventoryElem) {
        newInventoryElem.classList.remove('no-js-hidden');
        const ProductInventory = customElements.get('product-inventory');
        if (ProductInventory) {
          // Set up component properties before initialization
          newInventoryElem.inventoryNotice = newInventoryElem.querySelector('.js-inventory-notice');
          newInventoryElem.urgencyMessage = newInventoryElem.querySelector('.js-inventory-urgency');
          newInventoryElem.indicatorBar = newInventoryElem.querySelector('.js-inventory-indicator');
          
          // Set up initial inventory data
          const inventoryData = JSON.parse(newInventoryElem.querySelector('[type="application/json"]').textContent);
          newInventoryElem.variantInventory = inventoryData;
          
          Object.setPrototypeOf(newInventoryElem, ProductInventory.prototype);
          
          // Update with first variant's inventory data
          if (inventoryData && inventoryData.length > 0) {
            const firstVariant = inventoryData[0];
            newInventoryElem.updateInventory(
              firstVariant.inventory_quantity,
              firstVariant.available,
              firstVariant.inventory_policy
            );
          }
          
          // Bind to variant picker events
          const variantPicker = this.content.querySelector('variant-picker');
          if (variantPicker) {
            variantPicker.addEventListener('on:variant:change', newInventoryElem.handleVariantChange.bind(newInventoryElem));
          }
        }
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    async updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        // Check if product has restricted add to cart app block
        const hasRestrictedBlock = this.productEl.querySelector('.restricted-add-to-cart-wrapper');

        if (hasRestrictedBlock) {
          // Render restricted add to cart functionality directly
          await this.renderRestrictedAddToCart();
        } else {
          const signUpForm = this.productEl.querySelector('.product-signup');
          if (signUpForm) {
            this.form.innerHTML = signUpForm.innerHTML;
          } else {
            this.footer.classList.add('quick-add__footer-message');
            this.form.innerHTML = `
              <div class="alert quick-add__alert bg-info-bg text-info-text">
                <div class="flex">
                  <div>
                    <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                      <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                      <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                    <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                  </div>
                </div>
              </div>`;
          }
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Renders restricted add to cart functionality for products with purchase restrictions
     */
    async renderRestrictedAddToCart() {
      const restrictedWrapper = this.productEl.querySelector('.restricted-add-to-cart-wrapper');
      if (!restrictedWrapper) return;

      const productId = restrictedWrapper.dataset.productId;
      const variantId = restrictedWrapper.dataset.variantId;
      const shop = window.Shopify?.shop || location.hostname;

      // Get customer ID if logged in
      let customerId = null;
      if (window.meta && window.meta.page && window.meta.page.customerId) {
        customerId = `gid://shopify/Customer/${window.meta.page.customerId}`;
      }

      // Show loading state
      this.form.innerHTML = `
        <div class="product-info__add-to-cart flex">
          <button type="button" class="btn btn--primary w-full is-loading" disabled>
            正在檢查...
          </button>
        </div>
      `;

      try {
        // Call the restricted product API
        const apiUrl = `${window.location.origin}/apps/api/purchase-rules/check?productId=gid://shopify/Product/${productId}&variantId=gid://shopify/ProductVariant/${variantId}&shop=${encodeURIComponent(shop)}${customerId ? '&customerId=' + encodeURIComponent(customerId) : ''}`;

        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data.error || !data.isRestricted) {
          // Not restricted or error - show regular add to cart
          this.renderQuickAddButton(variantId);
          return;
        }

        if (!data.canPurchase) {
          // Cannot purchase - show login button
          this.form.innerHTML = `
            <a href="/account/login" class="btn btn--primary w-full">
              登入購買
            </a>
          `;
          return;
        }

        // Can purchase - render with quantity restrictions if applicable
        if (data.maxQuantity) {
          // Check current cart for existing quantity
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          const existingItem = cartData.items.find(item => item.id.toString() === variantId);
          const existingQty = existingItem ? existingItem.quantity : 0;
          const remainingQty = data.maxQuantity - existingQty;

          if (remainingQty <= 0) {
            // Max reached
            this.form.innerHTML = `
              <div class="product-info__add-to-cart flex">
                <button type="button" class="btn btn--primary w-full" disabled>
                  已達到最大購買數量
                </button>
              </div>
            `;
            return;
          }

          // Render with max quantity
          this.renderQuickAddButton(variantId, remainingQty, data.maxQuantity);
        } else {
          // No max quantity
          this.renderQuickAddButton(variantId);
        }
      } catch (error) {
        console.error('Error checking restrictions:', error);
        // On error, show regular add to cart
        this.renderQuickAddButton(variantId);
      }
    }

    /**
     * Renders the quick add button with optional quantity restrictions
     */
    renderQuickAddButton(variantId, maxQty = null, totalMax = null) {
      const hasMaxQty = maxQty !== null;

      let formHTML = '<div class="product-info__add-to-cart flex qty-input--secondary-btn">';

      // Quantity selector
      formHTML += `
        <quantity-input class="inline-block">
          <label class="label visually-hidden" for="quick-add-qty">數量</label>
          <div class="qty-input qty-input--combined inline-flex items-center w-full">
            <button type="button" class="qty-input__btn btn btn--minus no-js-hidden" data-action="decrease">
              <span class="visually-hidden">減少數量</span>
            </button>
            <input
              type="number"
              class="qty-input__input input"
              id="quick-add-qty"
              name="quantity"
              min="1"
              ${hasMaxQty ? `max="${maxQty}"` : ''}
              value="1"
            />
            <button type="button" class="qty-input__btn btn btn--plus no-js-hidden" data-action="increase">
              <span class="visually-hidden">增加數量</span>
            </button>
          </div>
        </quantity-input>
      `;

      // Add to cart button
      formHTML += `
        <div class="product-info__add-button">
          <button
            type="button"
            name="add"
            class="btn btn--primary w-full"
            data-variant-id="${variantId}"
            data-action="quick-add-to-cart"
          >
            加入購物車
          </button>
        </div>
      </div>`;

      this.form.innerHTML = formHTML;

      // Attach event listeners
      this.attachQuickAddListeners(maxQty, totalMax);
    }

    /**
     * Attaches event listeners for quick add button and quantity controls
     */
    attachQuickAddListeners(maxQty, totalMax) {
      const input = this.form.querySelector('#quick-add-qty');
      const minusBtn = this.form.querySelector('[data-action="decrease"]');
      const plusBtn = this.form.querySelector('[data-action="increase"]');
      const addBtn = this.form.querySelector('[data-action="quick-add-to-cart"]');

      if (!input || !minusBtn || !plusBtn || !addBtn) return;

      const hasMaxQty = maxQty !== null;

      // Update button states
      const updateButtonStates = () => {
        const currentVal = parseInt(input.value) || 1;
        if (currentVal < 1) input.value = 1;
        if (hasMaxQty && currentVal > maxQty) input.value = maxQty;

        const finalVal = parseInt(input.value);
        minusBtn.disabled = finalVal <= 1;
        plusBtn.disabled = hasMaxQty && finalVal >= maxQty;
      };

      // Quantity controls
      minusBtn.addEventListener('click', () => {
        const currentVal = parseInt(input.value) || 1;
        if (currentVal > 1) {
          input.value = currentVal - 1;
          updateButtonStates();
        }
      });

      plusBtn.addEventListener('click', () => {
        const currentVal = parseInt(input.value) || 1;
        const newVal = currentVal + 1;
        if (!hasMaxQty || newVal <= maxQty) {
          input.value = newVal;
          updateButtonStates();
        }
      });

      input.addEventListener('change', updateButtonStates);
      input.addEventListener('input', updateButtonStates);

      // Add to cart
      addBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const qty = parseInt(input.value);
        const variantId = addBtn.dataset.variantId;

        addBtn.disabled = true;
        addBtn.classList.add('is-loading');

        try {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', qty);

          // Add sections for cart drawer update
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            const cartSection = cartDrawer.closest('.shopify-section');
            if (cartSection) {
              const sectionId = cartSection.id.replace('shopify-section-', '');
              formData.append('sections', `cart-icon-bubble,${sectionId}`);
              formData.append('sections_url', window.location.pathname);
            }
          }

          const response = await fetch('/cart/add', {
            method: 'POST',
            headers: {
              'Accept': 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Failed to add to cart');
          }

          // Update cart drawer
          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data, true);
          }

          // Show success notification
          this.addedToCart();

          // If has max qty, refresh to update remaining quantity
          if (hasMaxQty) {
            setTimeout(() => this.updateForm(), 500);
          }

        } catch (error) {
          console.error('Error adding to cart:', error);
          alert(error.message || '無法加入購物車');
        } finally {
          addBtn.disabled = false;
          addBtn.classList.remove('is-loading');
        }
      });

      updateButtonStates();
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
